import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Upload, 
  ArrowRight, 
  ArrowLeft, 
  AlertTriangle, 
  Download, 
  RefreshCw, 
  HelpCircle,
  Database,
  FileCheck,
  Zap,
  X,
  Check,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  FileSpreadsheet,
  ShieldCheck,
  Layers,
  Save,
  Package,
  Boxes,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import logoRmaxWebp from '../assets/logo-rmax.webp';
import logoRmaxJpeg from '../assets/logo-rmax.jpeg';
import { 
  parseRawSpreadsheet, 
  parseBrlNumber,
  ColumnMapping,
  applyErpPreset,
  ERP_PRESETS
} from '../modules/erp/enterprise/migration/csvSpreadsheetParser';
import { 
  BatchItemInput, 
  TaxRegimeMode, 
  simulateBatch, 
  BatchSimulationSummary, 
  BatchItemSimulationResult,
  TaxReductionCategory,
  BusinessSector
} from '../lib/batchTaxSimulatorEngine';
import { useAuth } from '../contexts/AuthContext';
import { useExitIntent } from '../hooks/useExitIntent';
import BatchSimulatorExitIntentModal, { BatchSimulatorPlanKey } from '../components/BatchSimulatorExitIntentModal';
import CostCenterExportModal from '../components/tax-simulator/CostCenterExportModal';
import NicheTemplatesModal from '../components/tax-simulator/NicheTemplatesModal';
import InvoiceComparisonModal from '../components/tax-simulator/InvoiceComparisonModal';
import BatchSimulatorKpiCards from '../components/tax-simulator/BatchSimulatorKpiCards';
import BatchSimulatorMappingStep from '../components/tax-simulator/BatchSimulatorMappingStep';
import BatchSimulatorTable from '../components/tax-simulator/BatchSimulatorTable';
import {
  SavedBatchSimulationBundle,
  saveSimulationBundle,
  loadSimulationBundle,
  storePendingSimulation,
  setPendingCheckoutPlan,
  exportBatchToCostCenter,
  loadCatalogItemsIntoSimulator,
  fetchDatabaseTemplatesForSimulation,
  CostCenterExportMode
} from '../lib/batchSimulatorStorageBridge';

interface UploadedFileInfo {
  fileName: string;
  headers: string[];
  rows: Record<string, string>[];
  suggestedMappings: ColumnMapping;
}

export interface BatchTaxSimulatorPageProps {
  embeddedInPortal?: boolean;
  user?: any;
  materials?: any[];
  services?: any[];
  onSaveMaterials?: (materials: any[]) => Promise<void> | void;
  onSaveService?: (service: any) => Promise<any> | void;
  onBack?: () => void;
}

const DEMO_ITEMS: BatchItemInput[] = [
  { 
    sku: 'ALIM-01', 
    name: 'Arroz Tipo 1 - 5kg (Cesta Básica)', 
    ncm: '1006.30.21', 
    itemType: 'product',
    taxReduction: 'zero',
    costPrice: 20.00, 
    currentSalePrice: 26.00,
    supplierTaxRegime: 'regime_geral'
  },
  { 
    sku: 'MED-02', 
    name: 'Medicamento Genérico 500mg', 
    ncm: '3004.90.99', 
    itemType: 'product',
    taxReduction: 'reducao_60',
    costPrice: 15.00, 
    currentSalePrice: 20.00,
    supplierTaxRegime: 'regime_geral'
  },
  { 
    sku: 'CONF-03', 
    name: 'Camiseta Algodão P (Confecção)', 
    ncm: '6109.10.00', 
    itemType: 'product',
    taxReduction: 'padrao',
    costPrice: 35.00, 
    currentSalePrice: 48.00,
    supplierTaxRegime: 'regime_geral'
  },
  { 
    sku: 'SERV-04', 
    name: 'Consulta Clínica / Veterinária', 
    nbs: '1.2501.10.00', 
    itemType: 'service',
    taxReduction: 'reducao_60',
    costPrice: 40.00, 
    currentSalePrice: 80.00,
    supplierTaxRegime: 'regime_geral'
  },
  { 
    sku: 'SERV-05', 
    name: 'Assessoria Contábil / Jurídica (Prof. Regulamentada)', 
    nbs: '1.1501.20.00', 
    itemType: 'service',
    taxReduction: 'reducao_30',
    costPrice: 80.00, 
    currentSalePrice: 150.00,
    supplierTaxRegime: 'regime_geral'
  },
  { 
    sku: 'BEB-06', 
    name: 'Cerveja Artesanal Puro Malte 500ml', 
    ncm: '2203.00.00', 
    itemType: 'product',
    taxReduction: 'seletivo',
    costPrice: 4.50, 
    currentSalePrice: 6.00,
    supplierTaxRegime: 'regime_geral'
  },
];

export default function BatchTaxSimulatorPage({
  embeddedInPortal = false,
  user: propsUser,
  materials: propsMaterials = [],
  services: propsServices = [],
  onSaveMaterials,
  onSaveService,
  onBack
}: BatchTaxSimulatorPageProps = {}) {
  const navigate = useNavigate();
  const { session, isMasterUser } = useAuth();
  const activeUser = propsUser || session?.user;

  // Verificação de plano ativo para isenção de exit-intent e salvamento transparente
  const hasActivePlan = Boolean(
    isMasterUser ||
    activeUser?.isMasterUser ||
    activeUser?.tier === 'pro' ||
    activeUser?.tier === 'premium' ||
    activeUser?.tier === 'enterprise' ||
    activeUser?.plan === 'Pro' ||
    activeUser?.plan === 'Especialista' ||
    activeUser?.plan === 'Empresa' ||
    activeUser?.isTrialActive
  );

  const [step, setStep] = useState<'upload' | 'mapping' | 'results'>('upload');
  const [taxRegime, setTaxRegime] = useState<TaxRegimeMode>('simples_hibrido');
  const [targetMarginPct, setTargetMarginPct] = useState<number>(25);
  const [taxBenchmark, setTaxBenchmark] = useState<'tcu_2026' | 'fazenda_preliminar_8_8'>('tcu_2026');

  // Arquivos Carregados: Entrada e Saída
  const [entryFileInfo, setEntryFileInfo] = useState<UploadedFileInfo | null>(null);
  const [exitFileInfo, setExitFileInfo] = useState<UploadedFileInfo | null>(null);

  // Dados consolidados para o mapeamento
  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mappings, setMappings] = useState<ColumnMapping>({});
  const [fileName, setFileName] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Itens em simulação interativa + Separação estrita dos itens originais
  const [batchItems, setBatchItems] = useState<BatchItemInput[]>([]);
  const [originalBatchItems, setOriginalBatchItems] = useState<BatchItemInput[]>([]);
  const [results, setResults] = useState<BatchItemSimulationResult[]>([]);
  const [summary, setSummary] = useState<BatchSimulationSummary | null>(null);

  // Modal de Simulação de Nota Fiscal (Entrada vs Saída)
  const [editingModalIndex, setEditingModalIndex] = useState<number | null>(null);

  // Modais de Integração com o Banco de Dados e Centro de Custos R-MAX
  const [showCostCenterModal, setShowCostCenterModal] = useState<boolean>(false);
  const [costCenterExportMode, setCostCenterExportMode] = useState<CostCenterExportMode>('original');
  const [isExportingCostCenter, setIsExportingCostCenter] = useState<boolean>(false);
  const [showNicheTemplateModal, setShowNicheTemplateModal] = useState<boolean>(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);
  const [statusFeedback, setStatusFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  // Segmentação Operacional: Todos, Comércio / Revenda, Serviços, Indústria e Uso & Consumo
  const [selectedSector, setSelectedSector] = useState<'all' | 'comercio' | 'servico' | 'industria' | 'consumo'>('all');
  const [activePanelTab, setActivePanelTab] = useState<'entries' | 'exits' | 'consolidation'>('consolidation');
  const [showWorkingCapitalPanel, setShowWorkingCapitalPanel] = useState<boolean>(true);

  // Valor Hora de Sistema do R-MAX (soberano da empresa cliente)
  const systemBaseHourValue = propsUser?.financialConfig?.baseHourValue || propsUser?.baseHourValue || 50;

  // Filtragem dos resultados de acordo com o segmento ativo
  const filteredResults = useMemo(() => {
    if (selectedSector === 'all') return results;
    if (selectedSector === 'consumo') return results.filter(r => r.destination === 'consumption');
    return results.filter(r => r.sector === selectedSector && r.destination !== 'consumption');
  }, [results, selectedSector]);

  // Contadores por segmento para os botões do cabeçalho
  const sectorCounts = useMemo(() => {
    return {
      all: results.length,
      comercio: results.filter(r => r.sector === 'comercio' && r.destination !== 'consumption').length,
      servico: results.filter(r => r.sector === 'servico' && r.destination !== 'consumption').length,
      industria: results.filter(r => r.sector === 'industria' && r.destination !== 'consumption').length,
      consumo: results.filter(r => r.destination === 'consumption').length,
    };
  }, [results]);

  const entryFileInputRef = useRef<HTMLInputElement>(null);
  const exitFileInputRef = useRef<HTMLInputElement>(null);
  const unifiedFileInputRef = useRef<HTMLInputElement>(null);

  const formatBRL = (val: number) => {
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatPct = (val: number) => {
    return `${val.toFixed(1)}%`;
  };

  // Recalcular simulação sempre que parâmetros globais mudarem
  const recomputeSimulation = (
    items: BatchItemInput[],
    regime = taxRegime,
    margin = targetMarginPct,
    benchmark = taxBenchmark
  ) => {
    const simulated = simulateBatch(items, regime, margin, benchmark);
    setResults(simulated.items);
    setSummary(simulated.summary);
  };

  // 1. Hidratação inicial a partir de simulação prévia em nuvem/localStorage
  useEffect(() => {
    const saved = loadSimulationBundle(activeUser?.id);
    if (saved && saved.originalItems && saved.originalItems.length > 0 && batchItems.length === 0) {
      setBatchItems(saved.originalItems);
      setOriginalBatchItems(saved.originalItems);
      setTaxRegime(saved.taxRegime || 'simples_hibrido');
      setTargetMarginPct(saved.targetMarginPct || 25);
      setTaxBenchmark(saved.taxBenchmark || 'tcu_2026');
      setFileName(saved.fileName || 'simulacao_reforma_salva.csv');
      const sim = simulateBatch(
        saved.originalItems,
        saved.taxRegime || 'simples_hibrido',
        saved.targetMarginPct || 25,
        saved.taxBenchmark || 'tcu_2026'
      );
      setResults(sim.items);
      setSummary(sim.summary);
      setStep('results');
    }
  }, [activeUser?.id]);

  // 2. Autosave contínuo e silencioso quando houver dados preenchidos
  useEffect(() => {
    if (batchItems.length > 0) {
      const bundle: SavedBatchSimulationBundle = {
        id: `sim_${activeUser?.id || 'visitor'}`,
        userId: activeUser?.id,
        savedAt: new Date().toISOString(),
        taxRegime,
        targetMarginPct,
        taxBenchmark,
        originalItems: originalBatchItems.length > 0 ? originalBatchItems : batchItems,
        simulatedItems: results,
        fileName: fileName || 'simulacao_reforma_tributaria.csv',
      };
      saveSimulationBundle(bundle);
    }
  }, [batchItems, originalBatchItems, results, taxRegime, targetMarginPct, taxBenchmark, fileName, activeUser?.id]);

  // 3. Mecânica de Detecção de Saída (Exit-Intent)
  const isDirty = batchItems.length > 0;
  const { showModal: showExitModal, closeModal: closeExitModal, resetExitIntent } = useExitIntent({
    isDirty,
    isExcluded: hasActivePlan,
  });

  // Ação principal do Modal de Exit-Intent: Salvar e Checkout do Plano
  const handleProceedToSave = (planKey: BatchSimulatorPlanKey) => {
    const bundle: SavedBatchSimulationBundle = {
      id: `sim_${activeUser?.id || 'visitor'}_${Date.now()}`,
      userId: activeUser?.id,
      savedAt: new Date().toISOString(),
      taxRegime,
      targetMarginPct,
      taxBenchmark,
      originalItems: originalBatchItems.length > 0 ? originalBatchItems : batchItems,
      simulatedItems: results,
      fileName: fileName || 'simulacao_reforma_tributaria.csv',
    };
    saveSimulationBundle(bundle);
    storePendingSimulation(bundle);
    setPendingCheckoutPlan(planKey);
    closeExitModal();

    if (session?.user) {
      window.location.href = `/app?view=reforma_lote&action=checkout&plan=${encodeURIComponent(planKey)}`;
    } else {
      window.location.href = `/auth?redirect=${encodeURIComponent('/app?view=reforma_lote')}&plan=${encodeURIComponent(planKey)}`;
    }
  };

  // Ponte Direta para Cliente R-MAX existente: Preserva lote e redireciona ao login do portal
  const handleExistingCustomerLogin = () => {
    const bundle: SavedBatchSimulationBundle = {
      id: `sim_visitor_${Date.now()}`,
      savedAt: new Date().toISOString(),
      taxRegime,
      targetMarginPct,
      taxBenchmark,
      originalItems: originalBatchItems.length > 0 ? originalBatchItems : batchItems,
      simulatedItems: results,
      fileName: fileName || 'simulacao_reforma_tributaria.csv',
    };
    storePendingSimulation(bundle);
    closeExitModal();
    window.location.href = `/auth?redirect=${encodeURIComponent('/app?view=reforma_lote')}`;
  };

  const handleDiscardAndLeave = () => {
    closeExitModal();
  };

  // 4. Integração com o Banco de Dados R-MAX: Puxar do Catálogo Cadastrado
  const handleLoadCatalogFromPortal = () => {
    if (propsMaterials.length === 0 && propsServices.length === 0) {
      setStatusFeedback({
        type: 'info',
        message: 'Nenhum item cadastrado no Centro de Custos. Cadastre materiais ou importe dados.',
      });
      return;
    }
    const converted = loadCatalogItemsIntoSimulator(propsMaterials, propsServices);
    setBatchItems(converted);
    setOriginalBatchItems(JSON.parse(JSON.stringify(converted)));
    setFileName(`catalogo_rmax_${converted.length}_itens.csv`);
    recomputeSimulation(converted, taxRegime, targetMarginPct, taxBenchmark);
    setStep('results');
    setStatusFeedback({
      type: 'success',
      message: `${converted.length} itens do seu catálogo R-MAX foram carregados com sucesso!`,
    });
  };

  // 5. Integração com Modelos Fiscais por Nicho diretamente do Banco Supabase/Local
  const handleSelectNicheTemplates = async (niche: string) => {
    setIsLoadingTemplates(true);
    try {
      const templates = await fetchDatabaseTemplatesForSimulation(niche);
      if (templates.length === 0) {
        setStatusFeedback({
          type: 'info',
          message: 'Nenhum modelo fiscal encontrado para este segmento no momento.',
        });
        return;
      }
      setBatchItems(templates);
      setOriginalBatchItems(JSON.parse(JSON.stringify(templates)));
      setFileName(`modelos_banco_${niche}.csv`);
      recomputeSimulation(templates, taxRegime, targetMarginPct, taxBenchmark);
      setShowNicheTemplateModal(false);
      setStep('results');
      setStatusFeedback({
        type: 'success',
        message: `${templates.length} modelos fiscais carregados do banco de dados!`,
      });
    } catch (e: any) {
      setStatusFeedback({
        type: 'error',
        message: 'Falha ao carregar modelos fiscais do banco de dados.',
      });
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // 6. Confirmação de Exportação para o Centro de Custos R-MAX (Opção Seguro vs Simulado)
  const handleExportToCostCenterConfirm = async () => {
    if (results.length === 0) return;
    setIsExportingCostCenter(true);
    try {
      const exported = await exportBatchToCostCenter({
        items: results,
        results,
        originalItems: originalBatchItems,
        mode: costCenterExportMode,
        existingMaterials: propsMaterials,
        existingServices: propsServices,
      });

      if (onSaveMaterials && exported.materials.length > 0) {
        await onSaveMaterials(exported.materials);
      }
      if (onSaveService && exported.services.length > 0) {
        for (const s of exported.services) {
          await onSaveService(s);
        }
      }

      setShowCostCenterModal(false);
      setStatusFeedback({
        type: 'success',
        message: `${exported.materials.length + exported.services.length} itens exportados para o Centro de Custos no modo [${
          costCenterExportMode === 'original' 
            ? 'Seguro: Custos e Preços Originais Preservados' 
            : 'Atualizado: Novo Preço Sugerido CBS/IBS'
        }]!`,
      });
    } catch (err: any) {
      setStatusFeedback({
        type: 'error',
        message: err.message || 'Erro ao exportar itens para o Centro de Custos.',
      });
    } finally {
      setIsExportingCostCenter(false);
    }
  };

  // Atualizar item individual na simulação (Edição Interativa)
  const handleUpdateItem = (index: number, updates: Partial<BatchItemInput>) => {
    setBatchItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...updates };
      recomputeSimulation(updated, taxRegime, targetMarginPct, taxBenchmark);
      return updated;
    });
  };

  // Alternar regime fiscal no passo de resultados
  const handleRegimeChange = (newRegime: TaxRegimeMode) => {
    setTaxRegime(newRegime);
    recomputeSimulation(batchItems, newRegime, targetMarginPct, taxBenchmark);
  };

  // Alternar benchmark fiscal (TCU vs Fazenda)
  const handleBenchmarkChange = (newBenchmark: 'tcu_2026' | 'fazenda_preliminar_8_8') => {
    setTaxBenchmark(newBenchmark);
    recomputeSimulation(batchItems, taxRegime, targetMarginPct, newBenchmark);
  };

  // Alternar margem alvo global
  const handleTargetMarginChange = (newMargin: number) => {
    setTargetMarginPct(newMargin);
    recomputeSimulation(batchItems, taxRegime, newMargin, taxBenchmark);
  };

  // 1. Downloads de Modelos CSV (com suporte a Comércio, Serviço, Indústria, CFOP e Prazos)
  const handleDownloadEntryTemplate = () => {
    const csvContent = 
      "Codigo_SKU;Descricao_Item;NCM_ou_NBS;Preco_Custo_Compra;Regime_Fornecedor;CFOP;Prazo_Fornecedor_Dias;Segmento\n" +
      "SKU-001;Arroz 5kg Cesta Basica;1006.30.21;20,00;regime_geral;1102;28;comercio\n" +
      "SKU-002;Camiseta Algodao P;6109.10.00;35,00;regime_geral;1102;30;comercio\n" +
      "SKU-003;Medicamento Generico;3004.90.99;15,00;regime_geral;1102;21;comercio\n" +
      "SKU-004;Consulta Clinica;1.2501.10.00;40,00;regime_geral;1933;15;servico\n" +
      "SKU-005;Assessoria Contabil e Tributaria;1.1501.20.00;80,00;regime_geral;1933;30;servico\n" +
      "SKU-006;Manta Filtrante C/20 metros;5602.29.00;499,50;regime_geral;1101;30;industria\n" +
      "SKU-007;Bobina De Plastico Bolha 1,3x100;3921.19.00;88,72;regime_geral;1101;28;industria\n";

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'modelo_notas_de_entrada_compras.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadExitTemplate = () => {
    const csvContent = 
      "Codigo_SKU;Descricao_Item;Tipo_Item;NCM_ou_NBS;Reducao_Aliquota;Preco_Venda_Atual;CFOP;Prazo_Venda_Dias;Segmento\n" +
      "SKU-001;Arroz 5kg Cesta Basica;Produto;1006.30.21;zero;26,00;5102;15;comercio\n" +
      "SKU-002;Camiseta Algodao P;Produto;6109.10.00;padrao;48,00;5102;30;comercio\n" +
      "SKU-003;Medicamento Generico;Produto;3004.90.99;reducao_60;20,00;5102;28;comercio\n" +
      "SKU-004;Consulta Clinica;Servico;1.2501.10.00;reducao_60;80,00;5933;1;servico\n" +
      "SKU-005;Assessoria Contabil;Servico;1.1501.20.00;reducao_30;150,00;5933;30;servico\n" +
      "SKU-006;Manta Filtrante C/20 metros;Produto;5602.29.00;padrao;670,00;5101;45;industria\n" +
      "SKU-007;Bobina De Plastico Bolha 1,3x100;Produto;3921.19.00;padrao;120,00;5101;30;industria\n";

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'modelo_notas_de_saida_vendas.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadUnifiedTemplate = () => {
    const csvContent = 
      "Codigo_SKU;Descricao_Item;Tipo_Item;Codigo_Fiscal_NCM_NBS;Reducao_Aliquota;Preco_Custo_Compra;Preco_Venda_Atual;CFOP;Prazo_Venda_Dias;Segmento\n" +
      "SKU-001;Arroz 5kg Cesta Basica;Produto;1006.30.21;zero;20,00;26,00;5102;15;comercio\n" +
      "SKU-002;Camiseta Algodao P;Produto;6109.10.00;padrao;35,00;48,00;5102;30;comercio\n" +
      "SKU-003;Medicamento Generico;Produto;3004.90.99;reducao_60;15,00;20,00;5102;28;comercio\n" +
      "SKU-004;Consulta Clinica e Exames;Servico;1.2501.10.00;reducao_60;40,00;80,00;5933;1;servico\n" +
      "SKU-005;Honorarios Contabeis ou Juridicos;Servico;1.1501.20.00;reducao_30;80,00;150,00;5933;30;servico\n" +
      "SKU-006;Manta Filtrante C/20 metros;Produto;5602.29.00;padrao;499,50;670,00;5101;45;industria\n" +
      "SKU-007;Bobina De Plastico Bolha 1,3x100;Produto;3921.19.00;padrao;88,72;120,00;5101;30;industria\n";

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'modelo_unificado_reforma_tributaria.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 2. Leitura de Notas de Entrada
  const handleEntryFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        if (!text || text.trim().length === 0) throw new Error('O arquivo de entrada está vazio.');
        const parsed = parseRawSpreadsheet(text);
        if (parsed.headers.length === 0 || parsed.rows.length === 0) {
          throw new Error('Não foi possível identificar colunas no arquivo de entrada.');
        }

        const info: UploadedFileInfo = {
          fileName: file.name,
          headers: parsed.headers,
          rows: parsed.rows,
          suggestedMappings: parsed.suggestedMappings
        };

        setEntryFileInfo(info);

        // Se o arquivo de entrada também tiver preço de venda, marca como unificado
        const hasSalePrice = Object.values(parsed.suggestedMappings).includes('salePrice');
        if (hasSalePrice && !exitFileInfo) {
          setExitFileInfo({ ...info, fileName: `${file.name} (Entrada + Saída Detectados)` });
        }
      } catch (err: any) {
        setErrorMessage(err.message || 'Erro ao processar arquivo de entrada.');
      }
    };
    reader.readAsText(file);
  };

  // 3. Leitura de Notas de Saída
  const handleExitFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        if (!text || text.trim().length === 0) throw new Error('O arquivo de saída está vazio.');
        const parsed = parseRawSpreadsheet(text);
        if (parsed.headers.length === 0 || parsed.rows.length === 0) {
          throw new Error('Não foi possível identificar colunas no arquivo de saída.');
        }

        const info: UploadedFileInfo = {
          fileName: file.name,
          headers: parsed.headers,
          rows: parsed.rows,
          suggestedMappings: parsed.suggestedMappings
        };

        setExitFileInfo(info);

        // Se o arquivo de saída também tiver custo de compra, marca como unificado
        const hasCostPrice = Object.values(parsed.suggestedMappings).includes('costPrice');
        if (hasCostPrice && !entryFileInfo) {
          setEntryFileInfo({ ...info, fileName: `${file.name} (Entrada + Saída Detectados)` });
        }
      } catch (err: any) {
        setErrorMessage(err.message || 'Erro ao processar arquivo de saída.');
      }
    };
    reader.readAsText(file);
  };

  // 4. Leitura de Planilha Unificada
  const handleUnifiedFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        if (!text || text.trim().length === 0) throw new Error('O arquivo enviado está vazio.');
        const parsed = parseRawSpreadsheet(text);
        if (parsed.headers.length === 0 || parsed.rows.length === 0) {
          throw new Error('Não foi possível identificar colunas ou linhas no arquivo.');
        }

        const info: UploadedFileInfo = {
          fileName: file.name,
          headers: parsed.headers,
          rows: parsed.rows,
          suggestedMappings: parsed.suggestedMappings
        };

        setEntryFileInfo(info);
        setExitFileInfo(info);
        setFileName(file.name);
        setRawHeaders(parsed.headers);
        setRawRows(parsed.rows);
        setMappings(parsed.suggestedMappings);
        setStep('mapping');
      } catch (err: any) {
        setErrorMessage(err.message || 'Erro ao processar arquivo.');
      }
    };
    reader.readAsText(file);
  };

  // 5. Avançar da tela de Upload para Mapeamento
  const handleProceedToMapping = () => {
    if (!entryFileInfo && !exitFileInfo) {
      setErrorMessage('Por favor, carregue ao menos a planilha de Notas de Entrada ou de Notas de Saída.');
      return;
    }

    // Caso 1: Apenas um arquivo carregado (unificado)
    if (entryFileInfo && (!exitFileInfo || entryFileInfo.fileName === exitFileInfo.fileName)) {
      setFileName(entryFileInfo.fileName);
      setRawHeaders(entryFileInfo.headers);
      setRawRows(entryFileInfo.rows);
      setMappings(entryFileInfo.suggestedMappings);
      setStep('mapping');
      return;
    }

    if (exitFileInfo && !entryFileInfo) {
      setFileName(exitFileInfo.fileName);
      setRawHeaders(exitFileInfo.headers);
      setRawRows(exitFileInfo.rows);
      setMappings(exitFileInfo.suggestedMappings);
      setStep('mapping');
      return;
    }

    // Caso 2: Dois arquivos separados (Entrada e Saída)
    if (entryFileInfo && exitFileInfo) {
      // Cruzamento inteligente por SKU ou índice
      const entrySkuCol = Object.keys(entryFileInfo.suggestedMappings).find(h => entryFileInfo.suggestedMappings[h] === 'sku');
      const entryCostCol = Object.keys(entryFileInfo.suggestedMappings).find(h => entryFileInfo.suggestedMappings[h] === 'costPrice');
      const entryNameCol = Object.keys(entryFileInfo.suggestedMappings).find(h => entryFileInfo.suggestedMappings[h] === 'name');
      const entryNcmCol = Object.keys(entryFileInfo.suggestedMappings).find(h => entryFileInfo.suggestedMappings[h] === 'ncm');
      const entryNbsCol = Object.keys(entryFileInfo.suggestedMappings).find(h => entryFileInfo.suggestedMappings[h] === 'nbs');

      const exitSkuCol = Object.keys(exitFileInfo.suggestedMappings).find(h => exitFileInfo.suggestedMappings[h] === 'sku');
      const exitPriceCol = Object.keys(exitFileInfo.suggestedMappings).find(h => exitFileInfo.suggestedMappings[h] === 'salePrice');
      const exitNameCol = Object.keys(exitFileInfo.suggestedMappings).find(h => exitFileInfo.suggestedMappings[h] === 'name');
      const exitNcmCol = Object.keys(exitFileInfo.suggestedMappings).find(h => exitFileInfo.suggestedMappings[h] === 'ncm');
      const exitNbsCol = Object.keys(exitFileInfo.suggestedMappings).find(h => exitFileInfo.suggestedMappings[h] === 'nbs');
      const exitRedCol = Object.keys(exitFileInfo.suggestedMappings).find(h => exitFileInfo.suggestedMappings[h] === 'taxReduction');
      const exitCfopCol = Object.keys(exitFileInfo.suggestedMappings).find(h => exitFileInfo.suggestedMappings[h] === 'cfop');
      const exitPmrCol = Object.keys(exitFileInfo.suggestedMappings).find(h => exitFileInfo.suggestedMappings[h] === 'paymentTermDays');
      const entryPmpCol = Object.keys(entryFileInfo.suggestedMappings).find(h => entryFileInfo.suggestedMappings[h] === 'purchaseTermDays');
      const exitSectorCol = Object.keys(exitFileInfo.suggestedMappings).find(h => exitFileInfo.suggestedMappings[h] === 'sector');

      // Indexa entradas por SKU ou Nome
      const entryMap = new Map<string, Record<string, string>>();
      entryFileInfo.rows.forEach((r, idx) => {
        const key = (entrySkuCol && r[entrySkuCol]) ? r[entrySkuCol].trim().toUpperCase() : `INDEX_${idx}`;
        entryMap.set(key, r);
        if (entryNameCol && r[entryNameCol]) {
          entryMap.set(r[entryNameCol].trim().toLowerCase(), r);
        }
      });

      // Cruza com as saídas
      const mergedItems: BatchItemInput[] = exitFileInfo.rows.map((exitRow, idx) => {
        const exitSku = (exitSkuCol && exitRow[exitSkuCol]) ? exitRow[exitSkuCol].trim().toUpperCase() : `INDEX_${idx}`;
        const exitName = (exitNameCol && exitRow[exitNameCol]) ? exitRow[exitNameCol].trim().toLowerCase() : '';
        
        const matchedEntry = entryMap.get(exitSku) || entryMap.get(exitName) || entryFileInfo.rows[idx] || {};

        const rawCost = entryCostCol ? parseBrlNumber(matchedEntry[entryCostCol]) : 0;
        const rawPrice = exitPriceCol ? parseBrlNumber(exitRow[exitPriceCol]) : 0;
        const ncmVal = (exitNcmCol && exitRow[exitNcmCol]) || (entryNcmCol && matchedEntry[entryNcmCol]);
        const nbsVal = (exitNbsCol && exitRow[exitNbsCol]) || (entryNbsCol && matchedEntry[entryNbsCol]);
        const rawRed = exitRedCol ? (exitRow[exitRedCol] || '').toLowerCase() : '';
        const cfopVal = (exitCfopCol && exitRow[exitCfopCol]) || undefined;
        const pmrVal = (exitPmrCol && exitRow[exitPmrCol]) ? parseBrlNumber(exitRow[exitPmrCol]) : undefined;
        const pmpVal = (entryPmpCol && matchedEntry[entryPmpCol]) ? parseBrlNumber(matchedEntry[entryPmpCol]) : undefined;
        const sectorRaw = (exitSectorCol && exitRow[exitSectorCol]) ? exitRow[exitSectorCol].toLowerCase() : '';
        let sector: BusinessSector | undefined;
        if (sectorRaw.includes('serv')) sector = 'servico';
        else if (sectorRaw.includes('ind') || sectorRaw.includes('fabr')) sector = 'industria';
        else if (sectorRaw.includes('com') || sectorRaw.includes('rev')) sector = 'comercio';

        let taxReduction: TaxReductionCategory | undefined;
        if (rawRed.includes('zero') || rawRed.includes('100')) taxReduction = 'zero';
        else if (rawRed.includes('60')) taxReduction = 'reducao_60';
        else if (rawRed.includes('30')) taxReduction = 'reducao_30';
        else if (rawRed.includes('seletivo')) taxReduction = 'seletivo';

        return {
          sku: (exitSkuCol && exitRow[exitSkuCol]) || `SKU-${idx + 1}`,
          name: (exitNameCol && exitRow[exitNameCol]) || (entryNameCol && matchedEntry[entryNameCol]) || `Item ${idx + 1}`,
          costPrice: rawCost,
          currentSalePrice: rawPrice,
          ncm: ncmVal,
          nbs: nbsVal,
          taxReduction,
          supplierTaxRegime: 'regime_geral',
          cfop: cfopVal,
          paymentTermDays: pmrVal,
          purchaseTermDays: pmpVal,
          sector,
        };
      });

      setBatchItems(mergedItems);
      setOriginalBatchItems(JSON.parse(JSON.stringify(mergedItems)));
      recomputeSimulation(mergedItems, taxRegime, targetMarginPct, taxBenchmark);
      setFileName(`${entryFileInfo.fileName} + ${exitFileInfo.fileName}`);
      setStep('results');
    }
  };

  // 6. Carregar dados de demonstração (Produtos + Serviços)
  const handleLoadDemo = () => {
    setBatchItems(DEMO_ITEMS);
    setOriginalBatchItems(JSON.parse(JSON.stringify(DEMO_ITEMS)));
    recomputeSimulation(DEMO_ITEMS, taxRegime, targetMarginPct, taxBenchmark);
    setFileName('catalogo_demonstracao_6_skus.csv');
    setStep('results');
  };

  // 7. Executar mapeamento "De / Para" quando for arquivo unificado
  const handleProcessMapping = () => {
    const nameCol = Object.keys(mappings).find(h => mappings[h] === 'name');
    const costCol = Object.keys(mappings).find(h => mappings[h] === 'costPrice');
    const unitCostCol = Object.keys(mappings).find(h => mappings[h] === 'unitCost');
    const priceCol = Object.keys(mappings).find(h => mappings[h] === 'salePrice');
    const ncmCol = Object.keys(mappings).find(h => mappings[h] === 'ncm');
    const nbsCol = Object.keys(mappings).find(h => mappings[h] === 'nbs');
    const fiscalCol = Object.keys(mappings).find(h => mappings[h] === 'fiscalCode');
    const skuCol = Object.keys(mappings).find(h => mappings[h] === 'sku');
    const itemTypeCol = Object.keys(mappings).find(h => mappings[h] === 'itemType');
    const reductionCol = Object.keys(mappings).find(h => mappings[h] === 'taxReduction');
    const cfopCol = Object.keys(mappings).find(h => mappings[h] === 'cfop');
    const legacyIcmsCol = Object.keys(mappings).find(h => mappings[h] === 'legacyIcms');
    const legacyPisCol = Object.keys(mappings).find(h => mappings[h] === 'legacyPis');
    const legacyCofinsCol = Object.keys(mappings).find(h => mappings[h] === 'legacyCofins');
    const legacyIssCol = Object.keys(mappings).find(h => mappings[h] === 'legacyIss');
    const pmrCol = Object.keys(mappings).find(h => mappings[h] === 'paymentTermDays');
    const pmpCol = Object.keys(mappings).find(h => mappings[h] === 'purchaseTermDays');
    const sectorCol = Object.keys(mappings).find(h => mappings[h] === 'sector');

    if (!nameCol) {
      setErrorMessage('Por favor, certifique-se de mapear ao menos a coluna de Descrição do Item.');
      return;
    }

    const items: BatchItemInput[] = rawRows.map((row, idx) => {
      const rawReduction = reductionCol ? (row[reductionCol] || '').toLowerCase() : '';
      let taxReduction: TaxReductionCategory | undefined;
      if (rawReduction.includes('zero') || rawReduction.includes('100') || rawReduction.includes('cesta')) {
        taxReduction = 'zero';
      } else if (rawReduction.includes('60') || rawReduction.includes('saude') || rawReduction.includes('saúde') || rawReduction.includes('med')) {
        taxReduction = 'reducao_60';
      } else if (rawReduction.includes('30') || rawReduction.includes('prof') || rawReduction.includes('regulamentad')) {
        taxReduction = 'reducao_30';
      } else if (rawReduction.includes('seletivo')) {
        taxReduction = 'seletivo';
      }

      const rawType = itemTypeCol ? (row[itemTypeCol] || '').toLowerCase() : '';
      const itemType: 'product' | 'service' | undefined = 
        rawType.includes('serv') ? 'service' : rawType.includes('prod') || rawType.includes('merc') ? 'product' : undefined;

      const cfopVal = cfopCol ? row[cfopCol] : undefined;
      const pmrVal = pmrCol ? parseBrlNumber(row[pmrCol]) : undefined;
      const pmpVal = pmpCol ? parseBrlNumber(row[pmpCol]) : undefined;
      const unitCostVal = unitCostCol ? parseBrlNumber(row[unitCostCol]) : undefined;
      const legacyIcmsVal = legacyIcmsCol ? parseBrlNumber(row[legacyIcmsCol]) : undefined;
      const legacyPisVal = legacyPisCol ? parseBrlNumber(row[legacyPisCol]) : undefined;
      const legacyCofinsVal = legacyCofinsCol ? parseBrlNumber(row[legacyCofinsCol]) : undefined;
      const legacyIssVal = legacyIssCol ? parseBrlNumber(row[legacyIssCol]) : undefined;

      const sectorRaw = sectorCol ? (row[sectorCol] || '').toLowerCase() : '';
      let sector: BusinessSector | undefined;
      if (sectorRaw.includes('serv')) sector = 'servico';
      else if (sectorRaw.includes('ind') || sectorRaw.includes('fabr')) sector = 'industria';
      else if (sectorRaw.includes('com') || sectorRaw.includes('rev')) sector = 'comercio';

      return {
        sku: skuCol ? row[skuCol] : `SKU-${idx + 1}`,
        name: row[nameCol] || `Item ${idx + 1}`,
        costPrice: costCol ? parseBrlNumber(row[costCol]) : 0,
        unitCost: unitCostVal,
        currentSalePrice: priceCol ? parseBrlNumber(row[priceCol]) : 0,
        ncm: ncmCol ? row[ncmCol] : undefined,
        nbs: nbsCol ? row[nbsCol] : undefined,
        fiscalCode: fiscalCol ? row[fiscalCol] : undefined,
        itemType,
        taxReduction,
        supplierTaxRegime: 'regime_geral',
        cfop: cfopVal,
        legacyIcmsVal,
        legacyPisVal,
        legacyCofinsVal,
        legacyIssVal,
        paymentTermDays: pmrVal,
        purchaseTermDays: pmpVal,
        sector,
      };
    });

    setBatchItems(items);
    setOriginalBatchItems(JSON.parse(JSON.stringify(items)));
    recomputeSimulation(items, taxRegime, targetMarginPct, taxBenchmark);

    // Seleciona a aba visual mais relevante com base no perfil dos dados importados
    const hasAnyCost = items.some(it => it.costPrice > 0);
    const hasAnySale = items.some(it => it.currentSalePrice > 0);

    if (hasAnyCost && !hasAnySale) {
      setActivePanelTab('entries');
    } else if (!hasAnyCost && hasAnySale) {
      setActivePanelTab('exits');
    } else {
      setActivePanelTab('consolidation');
    }

    setStep('results');
  };

  // 8. Exportar CSV recalculado e auditado para ERPs
  const handleExportCsv = () => {
    if (results.length === 0) return;

    const headers = [
      'SKU',
      'Descricao',
      'Segmento',
      'Destinacao_Operacao',
      'CFOP',
      'NCM',
      'NBS',
      'Tipo_Item',
      'Prazo_Cliente_PMR_Dias',
      'Prazo_Fornecedor_PMP_Dias',
      'Regime_Reducao',
      'NF_Entrada_Custo_Total',
      'Custo_Unitario_Aquisicao',
      'Credito_Atual_Legado',
      'Credito_Vedado_Hoje',
      'Regime_Fornecedor_Entrada',
      'Aliquota_Entrada_Total_Pct',
      'Aliquota_Entrada_CBS_Pct',
      'Aliquota_Entrada_IBS_Pct',
      'Credito_Entrada_CBS',
      'Credito_Entrada_IBS',
      'Credito_Entrada_Total_Reforma',
      'Ganho_Liquido_Reforma',
      'Custo_Liquido_Estoque',
      'Preco_Venda_Atual',
      'Novo_Preco_Sugerido',
      'Preco_NF_Saida_Simulado',
      'Aliquota_Saida_Total_Pct',
      'Aliquota_Saida_CBS_Pct',
      'Aliquota_Saida_IBS_Pct',
      'Debito_Saida_CBS',
      'Debito_Saida_IBS',
      'Debito_Saida_Total',
      'Imposto_Liquido_CBS_A_Recolher',
      'Imposto_Liquido_IBS_A_Recolher',
      'Imposto_Liquido_Total_Split',
      'Sobra_Bruta_Projetada',
      'Margem_Projetada_Pct',
      'Status_Margem'
    ];

    const rows = results.map(r => [
      `"${r.sku}"`,
      `"${r.name.replace(/"/g, '""')}"`,
      `"${r.sector || 'comercio'}"`,
      `"${r.destination === 'consumption' ? 'Uso_Consumo' : r.destination === 'raw_material' ? 'Materia_Prima' : r.destination === 'service' ? 'Servico' : 'Revenda'}"`,
      `"${r.cfop || ''}"`,
      `"${r.ncm && r.ncm !== '-' && r.fiscalType === 'NCM' ? r.ncm : ''}"`,
      `"${r.nbs && r.nbs !== '-' && r.fiscalType === 'NBS' ? r.nbs : ''}"`,
      `"${r.fiscalType === 'NBS' ? 'Serviço' : 'Produto'}"`,
      r.paymentTermDays !== undefined ? r.paymentTermDays : '',
      batchItems[r.originalIndex]?.purchaseTermDays !== undefined ? batchItems[r.originalIndex].purchaseTermDays : '',
      `"${r.reductionLabel}"`,
      r.originalCost.toFixed(2).replace('.', ','),
      r.unitCost !== undefined ? r.unitCost.toFixed(2).replace('.', ',') : '',
      r.legacyCreditVal.toFixed(2).replace('.', ','),
      r.isCreditDisallowedToday ? 'SIM_VEDADO' : 'NAO',
      `"${r.supplierTaxRegime}"`,
      `${r.inputCreditPct.toFixed(1)}%`,
      `${r.inputCreditCbsRatePct.toFixed(1)}%`,
      `${r.inputCreditIbsRatePct.toFixed(1)}%`,
      r.inputCreditCbsVal.toFixed(2).replace('.', ','),
      r.inputCreditIbsVal.toFixed(2).replace('.', ','),
      r.reformCreditVal.toFixed(2).replace('.', ','),
      r.reformCreditGainVal.toFixed(2).replace('.', ','),
      r.netStockCost.toFixed(2).replace('.', ','),
      r.currentSalePrice.toFixed(2).replace('.', ','),
      r.suggestedSalePrice.toFixed(2).replace('.', ','),
      r.simulatedSalePrice.toFixed(2).replace('.', ','),
      `${r.totalTaxRatePct.toFixed(1)}%`,
      `${r.cbsRatePct.toFixed(1)}%`,
      `${r.ibsRatePct.toFixed(1)}%`,
      r.taxOnSaleCbsVal.toFixed(2).replace('.', ','),
      r.taxOnSaleIbsVal.toFixed(2).replace('.', ','),
      r.taxOnSaleVal.toFixed(2).replace('.', ','),
      r.cbsToPayVal.toFixed(2).replace('.', ','),
      r.ibsToPayVal.toFixed(2).replace('.', ','),
      r.netTaxPayableVal.toFixed(2).replace('.', ','),
      r.projectedMarginVal.toFixed(2).replace('.', ','),
      `${r.projectedMarginPct.toFixed(1)}%`,
      `"${r.statusLabel}"`
    ]);

    const csvText = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(["\uFEFF" + csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `simulacao_reforma_tributaria_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const activeModalItem = editingModalIndex !== null ? batchItems[editingModalIndex] : null;
  const activeModalResult = editingModalIndex !== null ? results[editingModalIndex] : null;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 pb-16">
      
      {/* Header Institucional R-MAX (Suporta modo embutido no portal ou página pública) */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {embeddedInPortal ? (
              <button
                type="button"
                onClick={onBack || (() => navigate('/app'))}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Voltar ao R-MAX</span>
              </button>
            ) : (
              <Link to="/" className="flex items-center gap-2">
                <picture>
                  <source srcSet={logoRmaxWebp} type="image/webp" />
                  <img src={logoRmaxJpeg} alt="RESOLV MAX" className="h-8 w-auto object-contain" />
                </picture>
                <span className="text-xs font-black uppercase tracking-wider text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md">
                  Simulador em Lote
                </span>
              </Link>
            )}
            {embeddedInPortal && (
              <span className="text-xs font-black uppercase tracking-wider text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-md">
                Reforma Tributária • Módulo Integrado
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!embeddedInPortal && (
              <>
                <Link
                  to="/calculadora-preco-venda"
                  className="text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors hidden sm:flex items-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Calculadora de 1 Item</span>
                </Link>

                {session?.user ? (
                  <Link
                    to="/app?view=reforma_lote"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-bold border border-emerald-200 transition-colors"
                  >
                    <Layers className="w-3.5 h-3.5 text-emerald-700" />
                    <span>Meu Painel R-MAX</span>
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={handleExistingCustomerLogin}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold border border-slate-300 transition-colors cursor-pointer"
                  >
                    <ShieldCheck className="w-3.5 h-3.5 text-slate-600" />
                    <span>Já sou cliente R-MAX</span>
                  </button>
                )}
              </>
            )}

            {embeddedInPortal && (
              <span className="text-xs font-mono text-slate-500 hidden sm:inline-block">
                Sessão Conectada: {activeUser?.ownerName || activeUser?.email || 'Usuário Autenticado'}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Conteúdo Principal */}
      <main className="max-w-6xl mx-auto px-4 pt-8">
        
        {/* Banner de Feedback Global */}
        {statusFeedback && (
          <div className={`mb-6 p-4 rounded-2xl border text-xs font-bold flex items-center justify-between animate-in fade-in duration-200 ${
            statusFeedback.type === 'success'
              ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
              : statusFeedback.type === 'error'
              ? 'bg-rose-50 text-rose-900 border-rose-200'
              : 'bg-blue-50 text-blue-900 border-blue-200'
          }`}>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-700" />
              <span>{statusFeedback.message}</span>
            </div>
            <button
              type="button"
              onClick={() => setStatusFeedback(null)}
              className="text-slate-400 hover:text-slate-700 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Banner Superior */}
        <div className="mb-8">
          <span className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-md mb-2">
            <Database className="w-3.5 h-3.5 text-emerald-700" />
            Auditoria em Massa de Catálogo & Reforma Tributária
          </span>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Simulador de Preço e Reforma em Lote (CSV & Interativo)
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-1 max-w-3xl leading-relaxed">
            Faça a auditoria e simulação de produtos (NCM) e serviços (NBS) de uma só vez. Apure créditos de compras na CBS e IBS, teste cenários de Notas de Entrada vs Notas de Saída e recalcule preços com a proteção técnica e comercial da margem.
          </p>
        </div>

        {/* ========================================================================= */}
        {/* PASSO 1: UPLOAD DE ARQUIVOS (DIVIDIDO EM NOTAS DE ENTRADA E NOTAS DE SAÍDA) */}
        {/* ========================================================================= */}
        {step === 'upload' && (
          <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm space-y-6">
            
            {/* Bloco de Configuração de Regime */}
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-800">
                  1. Configurações Fiscais da Simulação
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-500 font-medium">Benchmark de Alíquota:</span>
                  <select
                    value={taxBenchmark}
                    onChange={(e) => handleBenchmarkChange(e.target.value as any)}
                    className="px-2.5 py-1 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="tcu_2026">Oficial TCU (28,0% — 11,3% CBS + 16,7% IBS)</option>
                    <option value="fazenda_preliminar_8_8">Fazenda Preliminar (26,5% — 8,8% CBS + 17,7% IBS)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setTaxRegime('simples_hibrido')}
                  className={`p-3 text-left rounded-xl border text-xs transition-all ${
                    taxRegime === 'simples_hibrido'
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <strong className="block font-bold">Simples Híbrido</strong>
                  <span className={`text-[10px] ${taxRegime === 'simples_hibrido' ? 'text-emerald-100' : 'text-slate-500'}`}>
                    Credita impostos na compra + CBS/IBS por fora. Protege o estoque.
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setTaxRegime('simples_normal')}
                  className={`p-3 text-left rounded-xl border text-xs transition-all ${
                    taxRegime === 'simples_normal'
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <strong className="block font-bold">Simples Normal</strong>
                  <span className={`text-[10px] ${taxRegime === 'simples_normal' ? 'text-emerald-100' : 'text-slate-500'}`}>
                    Sem crédito na compra (custo bruto no estoque) + recolhe no DAS.
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setTaxRegime('lucro_real')}
                  className={`p-3 text-left rounded-xl border text-xs transition-all ${
                    taxRegime === 'lucro_real'
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <strong className="block font-bold">Lucro Real / Presumido</strong>
                  <span className={`text-[10px] ${taxRegime === 'lucro_real' ? 'text-emerald-100' : 'text-slate-500'}`}>
                    Não-cumulatividade plena de CBS e IBS com crédito integral.
                  </span>
                </button>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-slate-200 text-xs">
                <span className="text-slate-600 font-medium">
                  Margem Bruta Alvo Desejada para o cálculo de preço:
                </span>
                <div className="flex items-center gap-2">
                  {[20, 25, 30, 40].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setTargetMarginPct(m)}
                      className={`px-3 py-1.5 rounded-lg font-bold text-xs border transition-colors ${
                        targetMarginPct === m
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                      }`}
                    >
                      {m}%
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* SEÇÃO 2: ENVIAR PLANILHAS FISCAIS (DIVIDIDA EM ENTRADA E SAÍDA) */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                <div>
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-800">
                    2. Enviar Planilhas Fiscais: Notas de Entrada & Notas de Saída
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Você pode enviar arquivos separados para compras (Entrada) e vendas (Saída) ou uma planilha unificada.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => unifiedFileInputRef.current?.click()}
                  className="text-[11px] font-bold text-emerald-800 hover:text-emerald-950 underline self-start sm:self-auto"
                >
                  Ou enviar arquivo único com Entrada e Saída juntos
                </button>
                <input
                  ref={unifiedFileInputRef}
                  type="file"
                  accept=".csv,.txt,.tsv"
                  onChange={handleUnifiedFileUpload}
                  className="hidden"
                />
              </div>

              {/* Grid com 2 Caixas: Notas de Entrada e Notas de Saída */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* 📥 CAIXA 1: NOTAS DE ENTRADA (COMPRAS / INSUMOS) */}
                <div className={`p-5 rounded-3xl border-2 transition-all flex flex-col justify-between ${
                  entryFileInfo 
                    ? 'bg-emerald-50/50 border-emerald-400' 
                    : 'bg-slate-50/60 border-dashed border-slate-300 hover:border-emerald-500'
                }`}>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center">
                          <ArrowDownLeft className="w-4 h-4 text-emerald-700" />
                        </div>
                        <div>
                          <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">
                            Notas de Entrada (Compras)
                          </h4>
                          <span className="text-[10px] text-slate-500">Custo de compra e créditos CBS/IBS</span>
                        </div>
                      </div>

                      {entryFileInfo && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-900">
                          <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                          Carregado
                        </span>
                      )}
                    </div>

                    {/* Área de Seleção */}
                    {!entryFileInfo ? (
                      <div
                        onClick={() => entryFileInputRef.current?.click()}
                        className="py-8 px-4 text-center cursor-pointer rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 transition-all space-y-2"
                      >
                        <Upload className="w-6 h-6 mx-auto text-emerald-700" />
                        <div className="text-xs font-bold text-slate-800">
                          Clique aqui para carregar CSV de Entrada
                        </div>
                        <p className="text-[10px] text-slate-400 max-w-xs mx-auto">
                          Planilha com código SKU, custos de compra e NCM/NBS para apurar créditos
                        </p>
                      </div>
                    ) : (
                      <div className="p-3.5 bg-white border border-emerald-200 rounded-2xl space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold text-slate-800 truncate max-w-[200px]" title={entryFileInfo.fileName}>
                            {entryFileInfo.fileName}
                          </span>
                          <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded">
                            {entryFileInfo.rows.length} itens encontrados
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => entryFileInputRef.current?.click()}
                          className="text-[11px] font-bold text-slate-500 hover:text-slate-800 block"
                        >
                          Trocar arquivo de entrada
                        </button>
                      </div>
                    )}

                    <input
                      ref={entryFileInputRef}
                      type="file"
                      accept=".csv,.txt,.tsv"
                      onChange={handleEntryFileUpload}
                      className="hidden"
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-200/60 mt-4 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleDownloadEntryTemplate}
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-600 hover:text-slate-900"
                    >
                      <Download className="w-3.5 h-3.5 text-slate-500" />
                      <span>Baixar Modelo de Entrada (.CSV)</span>
                    </button>
                    {entryFileInfo && (
                      <button
                        type="button"
                        onClick={() => setEntryFileInfo(null)}
                        className="text-[10px] font-bold text-rose-600 hover:underline"
                      >
                        Remover
                      </button>
                    )}
                  </div>
                </div>

                {/* 📤 CAIXA 2: NOTAS DE SAÍDA (VENDAS / FATURAMENTO) */}
                <div className={`p-5 rounded-3xl border-2 transition-all flex flex-col justify-between ${
                  exitFileInfo 
                    ? 'bg-indigo-50/50 border-indigo-400' 
                    : 'bg-slate-50/60 border-dashed border-slate-300 hover:border-indigo-500'
                }`}>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-indigo-100 text-indigo-800 flex items-center justify-center">
                          <ArrowUpRight className="w-4 h-4 text-indigo-700" />
                        </div>
                        <div>
                          <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">
                            Notas de Saída (Vendas)
                          </h4>
                          <span className="text-[10px] text-slate-500">Preços atuais e alíquotas da reforma</span>
                        </div>
                      </div>

                      {exitFileInfo && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-indigo-200 text-indigo-900">
                          <CheckCircle2 className="w-3 h-3 text-indigo-700" />
                          Carregado
                        </span>
                      )}
                    </div>

                    {/* Área de Seleção */}
                    {!exitFileInfo ? (
                      <div
                        onClick={() => exitFileInputRef.current?.click()}
                        className="py-8 px-4 text-center cursor-pointer rounded-2xl bg-white border border-slate-200 hover:border-indigo-400 transition-all space-y-2"
                      >
                        <Upload className="w-6 h-6 mx-auto text-indigo-700" />
                        <div className="text-xs font-bold text-slate-800">
                          Clique aqui para carregar CSV de Saída
                        </div>
                        <p className="text-[10px] text-slate-400 max-w-xs mx-auto">
                          Planilha com preços atuais de venda, NCM/NBS e enquadramento fiscal
                        </p>
                      </div>
                    ) : (
                      <div className="p-3.5 bg-white border border-indigo-200 rounded-2xl space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold text-slate-800 truncate max-w-[200px]" title={exitFileInfo.fileName}>
                            {exitFileInfo.fileName}
                          </span>
                          <span className="text-[10px] font-bold text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded">
                            {exitFileInfo.rows.length} itens encontrados
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => exitFileInputRef.current?.click()}
                          className="text-[11px] font-bold text-slate-500 hover:text-slate-800 block"
                        >
                          Trocar arquivo de saída
                        </button>
                      </div>
                    )}

                    <input
                      ref={exitFileInputRef}
                      type="file"
                      accept=".csv,.txt,.tsv"
                      onChange={handleExitFileUpload}
                      className="hidden"
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-200/60 mt-4 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleDownloadExitTemplate}
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-600 hover:text-slate-900"
                    >
                      <Download className="w-3.5 h-3.5 text-slate-500" />
                      <span>Baixar Modelo de Saída (.CSV)</span>
                    </button>
                    {exitFileInfo && (
                      <button
                        type="button"
                        onClick={() => setExitFileInfo(null)}
                        className="text-[10px] font-bold text-rose-600 hover:underline"
                      >
                        Remover
                      </button>
                    )}
                  </div>
                </div>

              </div>

              {errorMessage && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Botões de Ação Final no Upload */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={handleDownloadUnifiedTemplate}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Baixar Modelo (.CSV)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowNicheTemplateModal(true)}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors"
                  >
                    <Database className="w-3.5 h-3.5 text-slate-600" />
                    <span>Modelos do Banco por Nicho</span>
                  </button>

                  {(propsMaterials.length > 0 || propsServices.length > 0) && (
                    <button
                      type="button"
                      onClick={handleLoadCatalogFromPortal}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-900 border border-indigo-200 font-bold rounded-xl text-xs transition-colors"
                    >
                      <Layers className="w-3.5 h-3.5 text-indigo-700" />
                      <span>Puxar Meu Catálogo ({propsMaterials.length + propsServices.length} itens)</span>
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={handleLoadDemo}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors"
                  >
                    <FileCheck className="w-3.5 h-3.5 text-slate-600" />
                    <span>Testar Demo (6 itens)</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleProceedToMapping}
                    disabled={!entryFileInfo && !exitFileInfo}
                    className={`w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-6 py-2.5 font-bold rounded-xl text-xs transition-all ${
                      entryFileInfo || exitFileInfo
                        ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-md shadow-emerald-600/20 cursor-pointer'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    <span>Continuar Simulação</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* PASSO 2: ASSISTENTE "DE / PARA" (MAPEAMENTO INTELIGENTE) */}
        {/* ========================================================================= */}
        {step === 'mapping' && (
          <BatchSimulatorMappingStep
            fileName={fileName}
            rawRowsCount={rawRows.length}
            rawHeaders={rawHeaders}
            rawRows={rawRows}
            mappings={mappings}
            onMappingChange={(header, target) => {
              setMappings(prev => ({
                ...prev,
                [header]: target as any
              }));
            }}
            onApplyPreset={(presetId) => {
              const applied = applyErpPreset(rawHeaders, presetId);
              setMappings(applied);
            }}
            errorMessage={errorMessage}
            onBack={() => setStep('upload')}
            onProcessMapping={handleProcessMapping}
          />
        )}

        {/* ========================================================================= */}
        {/* PASSO 3: RESULTADOS ANALÍTICOS & SIMULAÇÃO INTERATIVA */}
        {/* ========================================================================= */}
        {step === 'results' && summary && (
          <div className="space-y-6">
            
            {/* Barra Superior de Ações e Filtros Rápidos */}
            <div className="bg-white rounded-3xl border border-slate-200 p-5 shadow-sm flex flex-col lg:flex-row items-center justify-between gap-4">
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-emerald-800">
                  Relatório Concluído ({summary.totalItems} Itens analisados)
                </span>
                <h3 className="text-base font-black text-slate-900 mt-0.5">
                  Diagnóstico Consolidado & Simulação Interativa de Notas Fiscais
                </h3>
              </div>

              {/* Controles Dinâmicos */}
              <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs">
                  <span className="text-slate-500 font-medium">Regime:</span>
                  <select
                    value={taxRegime}
                    onChange={(e) => handleRegimeChange(e.target.value as any)}
                    className="bg-transparent font-bold text-slate-800 focus:outline-none cursor-pointer"
                  >
                    <option value="simples_hibrido">Simples Híbrido (Com Crédito)</option>
                    <option value="simples_normal">Simples Normal (Sem Crédito)</option>
                    <option value="lucro_real">Lucro Real / Presumido</option>
                  </select>
                </div>

                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs">
                  <span className="text-slate-500 font-medium">Margem Alvo:</span>
                  <select
                    value={targetMarginPct}
                    onChange={(e) => handleTargetMarginChange(Number(e.target.value))}
                    className="bg-transparent font-bold text-slate-800 focus:outline-none cursor-pointer"
                  >
                    {[15, 20, 25, 30, 35, 40].map(m => (
                      <option key={m} value={m}>{m}%</option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  onClick={() => setStep('upload')}
                  className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Novo Arquivo</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowCostCenterModal(true)}
                  className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-900 border border-indigo-200 font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                  title="Exportar itens auditados diretamente para o Centro de Custos R-MAX"
                >
                  <Layers className="w-3.5 h-3.5 text-indigo-700" />
                  <span>Exportar p/ Centro de Custos</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const bundle: SavedBatchSimulationBundle = {
                      id: `sim_${activeUser?.id || 'visitor'}_${Date.now()}`,
                      userId: activeUser?.id,
                      savedAt: new Date().toISOString(),
                      taxRegime,
                      targetMarginPct,
                      taxBenchmark,
                      originalItems: originalBatchItems.length > 0 ? originalBatchItems : batchItems,
                      simulatedItems: results,
                      fileName: fileName || 'simulacao_reforma_tributaria.csv',
                    };
                    saveSimulationBundle(bundle);
                    setStatusFeedback({
                      type: 'success',
                      message: 'Simulação e parâmetros gravados com sucesso!',
                    });
                  }}
                  className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                  title="Salvar estado atual da simulação"
                >
                  <Save className="w-3.5 h-3.5 text-slate-600" />
                  <span>Salvar Análise</span>
                </button>

                <button
                  type="button"
                  onClick={handleExportCsv}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md shadow-emerald-600/20 transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Baixar Planilha Atualizada (.CSV)</span>
                </button>
              </div>
            </div>

            {/* Seletor Master de Painéis de Visualização: Entrada vs Saída vs Apuração Consolidada */}
            <div className="bg-white p-2.5 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-1.5 p-1 bg-slate-100 rounded-xl">
                <button
                  type="button"
                  onClick={() => setActivePanelTab('entries')}
                  className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                    activePanelTab === 'entries'
                      ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/20'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                  }`}
                >
                  <ArrowDownLeft className="w-4 h-4" />
                  <span>📥 Notas de Entrada & Insumos</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActivePanelTab('exits')}
                  className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                    activePanelTab === 'exits'
                      ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-600/20'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                  }`}
                >
                  <ArrowUpRight className="w-4 h-4" />
                  <span>📤 Notas de Saída & Catálogo</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActivePanelTab('consolidation')}
                  className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                    activePanelTab === 'consolidation'
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                  }`}
                >
                  <span>⚖️ Apuração Consolidada & Capital de Giro</span>
                </button>
              </div>

              <div className="text-[11px] text-slate-500 font-medium px-2">
                {activePanelTab === 'entries' && (
                  <span>Visão focada na <strong>recuperação de créditos de compras</strong> e destravamento de Uso & Consumo.</span>
                )}
                {activePanelTab === 'exits' && (
                  <span>Visão focada na <strong>precificação sugerida (degrau de 5)</strong> e débitos da Reforma.</span>
                )}
                {activePanelTab === 'consolidation' && (
                  <span>Visão executiva integrada de <strong>Split Payment</strong> e <strong>Capital de Giro</strong>.</span>
                )}
              </div>
            </div>

            {/* Painel Executivo Exclusivo de Compras & Recuperação de Créditos */}
            {activePanelTab === 'entries' && summary && (
              <div className="bg-gradient-to-br from-emerald-950 via-slate-900 to-teal-950 text-white p-5 rounded-3xl border border-emerald-700/40 shadow-sm space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-emerald-800/60 pb-3">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded-full border border-emerald-400/30">
                      Painel Executivo de Compras & Recuperação de Créditos
                    </span>
                    <h3 className="text-base font-black text-white mt-1">
                      Auditoria de Notas Fiscais de Entrada & Benefício da Não-Cumulatividade Plena
                    </h3>
                  </div>
                  <div className="text-left sm:text-right">
                    <span className="text-[10px] text-emerald-300 block">Total de Compras Analisadas</span>
                    <span className="text-xl font-black text-white">{formatBRL(summary.totalPurchasesVal)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-slate-800/70 p-3 rounded-2xl border border-slate-700">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block">Crédito Atual (Legado)</span>
                    <div className="text-lg font-black text-slate-200 mt-0.5">
                      {formatBRL(summary.totalLegacyCreditsVal)}
                    </div>
                    <span className="text-[10px] text-slate-400">ICMS + PIS + COFINS hoje</span>
                  </div>

                  <div className="bg-emerald-950/70 p-3 rounded-2xl border border-emerald-700/60">
                    <span className="text-[10px] uppercase font-bold text-emerald-300 block">Crédito Reforma (CBS+IBS)</span>
                    <div className="text-lg font-black text-emerald-400 mt-0.5">
                      {formatBRL(summary.totalReformCreditsVal || summary.totalInputCreditsGained)}
                    </div>
                    <span className="text-[10px] text-emerald-300/80">Alíquota padrão de 28%</span>
                  </div>

                  <div className="bg-emerald-500/20 p-3 rounded-2xl border border-emerald-400/50">
                    <span className="text-[10px] uppercase font-bold text-emerald-300 block">Ganho Líquido na Reforma</span>
                    <div className="text-xl font-black text-emerald-300 mt-0.5">
                      +{formatBRL(summary.totalNetCreditGainVal)}
                    </div>
                    <span className="text-[10px] text-emerald-200">Economia real gerada no caixa</span>
                  </div>

                  <div className="bg-slate-800/70 p-3 rounded-2xl border border-slate-700">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block">Uso & Consumo Destravado</span>
                    <div className="text-lg font-black text-amber-300 mt-0.5">
                      {summary.disallowedCreditsCount} <span className="text-xs font-normal text-slate-400">itens</span>
                    </div>
                    <span className="text-[10px] text-slate-400">Antes vedados pela LC 87/96</span>
                  </div>
                </div>
              </div>
            )}

            {/* Cards de KPIs Executivos para Saídas e Consolidação */}
            {activePanelTab !== 'entries' && (
              <BatchSimulatorKpiCards
                summary={summary}
                formatBRL={formatBRL}
                formatPct={formatPct}
              />
            )}

            {/* Cockpit de Capital de Giro & Impacto do Split Payment (LC 214/2025) */}
            {summary?.workingCapitalMetrics && activePanelTab === 'consolidation' && (
              <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm transition-all">
                <div 
                  onClick={() => setShowWorkingCapitalPanel(prev => !prev)}
                  className="p-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer select-none hover:opacity-95 transition-opacity"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-500/20 border border-indigo-400/30 rounded-xl">
                      <Clock className="w-5 h-5 text-indigo-300" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-black text-white">
                          Inteligência de Capital de Giro & Impacto do Split Payment
                        </h4>
                        <span className="text-[10px] font-black uppercase tracking-wider bg-indigo-500/30 text-indigo-200 border border-indigo-400/30 px-2 py-0.5 rounded-full">
                          LC 214/2025
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-300 mt-0.5">
                        Ciclo financeiro e impacto da retenção na fonte sobre o caixa imediato da empresa
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 self-end sm:self-center">
                    {/* Indicador de Valor Hora de Sistema do R-MAX (Soberano da Empresa Cliente) */}
                    <div className="flex items-center gap-1.5 px-3 py-1 bg-slate-800/80 border border-slate-700 rounded-xl text-xs font-medium text-slate-200" title="Taxa horária base configurada no R-MAX (soberano da empresa cliente)">
                      <span className="text-slate-400 text-[11px]">Valor Hora de Sistema:</span>
                      <strong className="text-emerald-400 font-bold">{formatBRL(systemBaseHourValue)}/h</strong>
                    </div>

                    <button 
                      type="button" 
                      className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
                      title={showWorkingCapitalPanel ? "Recolher painel" : "Expandir painel"}
                    >
                      {showWorkingCapitalPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {showWorkingCapitalPanel && (
                  <div className="p-4 sm:p-5 bg-slate-50/50 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      {/* 1. PMR Médio */}
                      <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-xs">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                          PMR (Vendas)
                        </span>
                        <div className="text-lg font-black text-slate-900 mt-1">
                          {summary.workingCapitalMetrics.avgPmrDays.toFixed(0)} <span className="text-xs font-normal text-slate-500">dias</span>
                        </div>
                        <span className="text-[10px] text-slate-500 block mt-0.5">
                          Prazo médio recebimento
                        </span>
                      </div>

                      {/* 2. PMP Médio */}
                      <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-xs">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                          PMP (Compras)
                        </span>
                        <div className="text-lg font-black text-slate-900 mt-1">
                          {summary.workingCapitalMetrics.avgPmpDays.toFixed(0)} <span className="text-xs font-normal text-slate-500">dias</span>
                        </div>
                        <span className="text-[10px] text-slate-500 block mt-0.5">
                          Prazo médio fornecedores
                        </span>
                      </div>

                      {/* 3. PME Estimado */}
                      <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-xs">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                          PME (Estoque)
                        </span>
                        <div className="text-lg font-black text-slate-900 mt-1">
                          {summary.workingCapitalMetrics.avgPmeDays.toFixed(0)} <span className="text-xs font-normal text-slate-500">dias</span>
                        </div>
                        <span className="text-[10px] text-slate-500 block mt-0.5">
                          Giro médio de estoque
                        </span>
                      </div>

                      {/* 4. Descasamento / Ciclo Financeiro */}
                      <div className={`p-3 rounded-2xl border shadow-xs ${
                        summary.workingCapitalMetrics.financialCycleDays > 0 
                          ? 'bg-amber-50/80 border-amber-200 text-amber-950'
                          : 'bg-emerald-50/80 border-emerald-200 text-emerald-950'
                      }`}>
                        <span className="text-[10px] font-bold uppercase tracking-wider block opacity-75">
                          Ciclo Financeiro
                        </span>
                        <div className="text-lg font-black mt-1">
                          {summary.workingCapitalMetrics.financialCycleDays > 0 ? `+${summary.workingCapitalMetrics.financialCycleDays.toFixed(0)}` : summary.workingCapitalMetrics.financialCycleDays.toFixed(0)} <span className="text-xs font-normal opacity-75">dias</span>
                        </div>
                        <span className="text-[10px] block mt-0.5">
                          {summary.workingCapitalMetrics.financialCycleDays > 0 ? 'Descasamento de caixa' : 'Ciclo autofinanciado'}
                        </span>
                      </div>

                      {/* 5. Dreno de Caixa Imediato Split */}
                      <div className="bg-rose-50/80 p-3 rounded-2xl border border-rose-200 text-rose-950 shadow-xs">
                        <span className="text-[10px] font-bold uppercase tracking-wider block text-rose-600">
                          Dreno Split Payment
                        </span>
                        <div className="text-lg font-black text-rose-700 mt-1">
                          {formatBRL(summary.workingCapitalMetrics.splitPaymentDrainVal)}
                        </div>
                        <span className="text-[10px] text-rose-600/80 block mt-0.5">
                          Retenção na fonte instantânea
                        </span>
                      </div>

                      {/* 6. Prazo Ideal de Venda Recomendado */}
                      <div className="bg-indigo-50/80 p-3 rounded-2xl border border-indigo-200 text-indigo-950 shadow-xs">
                        <span className="text-[10px] font-bold uppercase tracking-wider block text-indigo-600">
                          Prazo Ideal Venda
                        </span>
                        <div className="text-lg font-black text-indigo-900 mt-1">
                          {summary.workingCapitalMetrics.idealReceivingDaysForSplit.toFixed(0)} <span className="text-xs font-normal text-indigo-600">dias</span>
                        </div>
                        <span className="text-[10px] text-indigo-600/80 block mt-0.5">
                          Meta para neutralizar dreno
                        </span>
                      </div>
                    </div>

                    <div className="text-xs bg-white p-3.5 rounded-2xl border border-slate-200 text-slate-600 flex flex-col md:flex-row md:items-center justify-between gap-2">
                      <div className="flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span className="text-[11px] leading-relaxed">
                          <strong>Como o Split Payment afeta seu fluxo:</strong> Na sistemática anterior, o imposto era recolhido no dia 20 do mês seguinte, propiciando float temporário. Com o Split Payment automático (LC 214/2025), o valor líquido cai descontado na adquirente/banco no dia da liquidação. Para preservar a liquidez da empresa, ajuste seus prazos de venda para até <strong>{summary.workingCapitalMetrics.idealReceivingDaysForSplit.toFixed(0)} dias</strong> ou renegocie prazos com fornecedores.
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Barra de Filtros Segmentados por Setor Operacional */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setSelectedSector('all')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    selectedSector === 'all'
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  Todos os Itens ({sectorCounts.all})
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedSector('comercio')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    selectedSector === 'comercio'
                      ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/20'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  🛍️ Comércio / Revenda ({sectorCounts.comercio})
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedSector('servico')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    selectedSector === 'servico'
                      ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-600/20'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  💼 Serviços ({sectorCounts.servico})
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedSector('industria')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    selectedSector === 'industria'
                      ? 'bg-amber-600 text-white shadow-sm shadow-amber-600/20'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  🏭 Indústria ({sectorCounts.industria})
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedSector('consumo')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    selectedSector === 'consumo'
                      ? 'bg-slate-800 text-white shadow-sm shadow-slate-800/20'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                  }`}
                  title="Materiais de uso e consumo que antes não geravam crédito, mas geram crédito pleno na Reforma"
                >
                  📦 Uso & Consumo ({sectorCounts.consumo})
                </button>
              </div>

              {selectedSector !== 'all' && (
                <span className="text-[11px] font-medium text-slate-500">
                  Exibindo {filteredResults.length} de {results.length} itens filtrados
                </span>
              )}
            </div>

            {/* Tabela Analítica & Interativa de Produtos e Serviços (Subcomponente Modular) */}
            <BatchSimulatorTable
              results={filteredResults}
              batchItems={batchItems}
              activePanelTab={activePanelTab}
              taxRegime={taxRegime}
              targetMarginPct={targetMarginPct}
              onUpdateItem={handleUpdateItem}
              onOpenSimulationModal={(idx) => setEditingModalIndex(idx)}
              formatBRL={formatBRL}
              formatPct={formatPct}
            />

          </div>
        )}

        {/* ========================================================================= */}
        {/* MODAL: SIMULADOR DE NOTA FISCAL (ENTRADA VS SAÍDA) */}
        {/* ========================================================================= */}
        <InvoiceComparisonModal
          isOpen={editingModalIndex !== null && !!activeModalItem && !!activeModalResult}
          onClose={() => setEditingModalIndex(null)}
          activeItem={activeModalItem}
          activeResult={activeModalResult}
          onUpdateItem={(updates) => {
            if (editingModalIndex !== null) {
              handleUpdateItem(editingModalIndex, updates);
            }
          }}
          formatBRL={formatBRL}
          formatPct={formatPct}
        />

        {/* ========================================================================= */}
        {/* MODAL 1: INTENÇÃO DE SAÍDA E RETENÇÃO DE DADOS (MATERIAL DESIGN 3) */}
        {/* ========================================================================= */}
        <BatchSimulatorExitIntentModal
          isOpen={showExitModal}
          onClose={closeExitModal}
          itemsCount={batchItems.length}
          totalCreditsGained={summary?.totalInputCreditsGained || 0}
          totalSuggestedValue={results.reduce((acc, r) => acc + r.suggestedSalePrice, 0)}
          taxRegimeLabel={
            taxRegime === 'simples_hibrido' 
              ? 'Simples Híbrido (Com Crédito)' 
              : taxRegime === 'simples_normal' 
              ? 'Simples Normal' 
              : 'Lucro Real / Presumido'
          }
          onProceedToSave={handleProceedToSave}
          onExistingCustomerLogin={handleExistingCustomerLogin}
          onDiscardAndLeave={handleDiscardAndLeave}
        />

        {/* ========================================================================= */}
        {/* MODAL 2: EXPORTAÇÃO PARA O CENTRO DE CUSTOS (SEPARAÇÃO ORIGINAL VS SIMULADO) */}
        {/* ========================================================================= */}
        <CostCenterExportModal
          isOpen={showCostCenterModal}
          onClose={() => setShowCostCenterModal(false)}
          itemsCount={results.length}
          exportMode={costCenterExportMode}
          onModeChange={setCostCenterExportMode}
          onConfirm={handleExportToCostCenterConfirm}
          isExporting={isExportingCostCenter}
        />

        {/* ========================================================================= */}
        {/* MODAL 3: MODELOS FISCAIS POR NICHO (BANCO DE DADOS R-MAX) */}
        {/* ========================================================================= */}
        <NicheTemplatesModal
          isOpen={showNicheTemplateModal}
          onClose={() => setShowNicheTemplateModal(false)}
          onSelectNiche={handleSelectNicheTemplates}
          isLoading={isLoadingTemplates}
        />

      </main>
    </div>
  );
}
